import React, { useState, useEffect } from 'react';
import * as userApis from '../../apis/users';
import useDebounce from '../../utils/useDebounce';
import { MEDIA_SERVER_URL } from '../../constants/constants';
import { FaSearch, FaUserCircle, FaCheckCircle, FaTimes } from 'react-icons/fa';

const TriggerVerificationModal = ({ isOpen, onClose, onTrigger, initialType = 'identity_only' }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [message, setMessage] = useState('');
    const [type, setType] = useState(initialType);
    const [sending, setSending] = useState(false);

    const debouncedSearch = useDebounce(searchTerm, 500);

    useEffect(() => {
        if (debouncedSearch && isOpen) {
            fetchUsers();
        } else {
            setUsers([]);
        }
    }, [debouncedSearch, isOpen]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await userApis.getAllUsers({
                page: 1,
                searchTerm: debouncedSearch,
            });
            setUsers(response.users || []);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTrigger = async () => {
        if (!selectedUser) {
            alert('Please select a user first');
            return;
        }
        if (!message.trim()) {
            alert('Please enter a message for the user');
            return;
        }

        setSending(true);
        try {
            await onTrigger({
                userId: selectedUser.id,
                type,
                message
            });
            resetAndClose();
        } catch (error) {
            console.error('Error triggering verification:', error);
            alert('Failed to send request. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const resetAndClose = () => {
        setSearchTerm('');
        setUsers([]);
        setSelectedUser(null);
        setMessage('');
        setType(initialType);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={styles.title}>Trigger Verification Request</h2>
                    <button onClick={resetAndClose} style={styles.closeBtn}><FaTimes /></button>
                </div>

                <div style={styles.content}>
                    {/* User Search Section */}
                    <div style={styles.section}>
                        <label style={styles.label}>1. Search & Select User</label>
                        <div style={styles.searchContainer}>
                            <FaSearch style={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by username or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={styles.searchInput}
                            />
                        </div>

                        <div style={styles.userList}>
                            {loading ? (
                                <div style={styles.loader}>Searching...</div>
                            ) : users.length > 0 ? (
                                users.map(user => (
                                    <div
                                        key={user.id}
                                        style={{
                                            ...styles.userItem,
                                            backgroundColor: selectedUser?.id === user.id ? '#e3f2fd' : 'transparent',
                                            border: selectedUser?.id === user.id ? '1px solid #2196f3' : '1px solid #eee'
                                        }}
                                        onClick={() => setSelectedUser(user)}
                                    >
                                        <div style={styles.userInfo}>
                                            {user.profile_pic ? (
                                                <img src={user.profile_pic.startsWith('http') ? user.profile_pic : `${MEDIA_SERVER_URL}/${user.profile_pic}`} alt="" style={styles.avatar} />
                                            ) : (
                                                <FaUserCircle style={styles.avatarPlaceholder} />
                                            )}
                                            <div style={styles.userDetails}>
                                                <div style={styles.username}>{user.username}</div>
                                                <div style={styles.email}>{user.email}</div>
                                            </div>
                                        </div>
                                        {selectedUser?.id === user.id && <FaCheckCircle style={styles.checkIcon} />}
                                    </div>
                                ))
                            ) : searchTerm && !loading ? (
                                <div style={styles.noResults}>No users found</div>
                            ) : (
                                <div style={styles.placeholderText}>Start typing to search for a user</div>
                            )}
                        </div>
                    </div>

                    {/* Request Type Section */}
                    <div style={styles.section}>
                        <label style={styles.label}>2. Select Verification Type</label>
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            style={styles.select}
                        >
                            <option value="identity_only">Identity Verification</option>
                            <option value="basic_badge">Basic Badge</option>
                            <option value="black_badge">Black Badge</option>
                            <option value="agent_badge">Agent Badge</option>
                            <option value="business_badge">Business Badge</option>
                            <option value="premium_badge">Premium Badge</option>
                        </select>
                    </div>

                    {/* Message Section */}
                    <div style={styles.section}>
                        <label style={styles.label}>3. Custom Message (sent via Email/Notification)</label>
                        <textarea
                            placeholder="Type your message to the user here..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            style={styles.textarea}
                        />
                        <p style={styles.hint}>This message will be included in the official Dream App email and push notification.</p>
                    </div>
                </div>

                <div style={styles.footer}>
                    <button onClick={resetAndClose} style={styles.cancelBtn} disabled={sending}>Cancel</button>
                    <button
                        onClick={handleTrigger}
                        style={{
                            ...styles.sendBtn,
                            opacity: (!selectedUser || !message.trim() || sending) ? 0.6 : 1,
                            cursor: (!selectedUser || !message.trim() || sending) ? 'not-allowed' : 'pointer'
                        }}
                        disabled={!selectedUser || !message.trim() || sending}
                    >
                        {sending ? 'Sending...' : 'Send Request'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        backdropFilter: 'blur(4px)',
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        width: '500px',
        maxWidth: '90%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        animation: 'modalFadeIn 0.3s ease-out',
    },
    header: {
        padding: '20px 24px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: '#fff',
    },
    title: {
        margin: 0,
        fontSize: '1.25rem',
        fontWeight: '600',
    },
    closeBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: 'none',
        color: '#fff',
        fontSize: '1.2rem',
        cursor: 'pointer',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'background 0.2s',
    },
    content: {
        padding: '24px',
        overflowY: 'auto',
        flex: 1,
    },
    section: {
        marginBottom: '24px',
    },
    label: {
        display: 'block',
        fontSize: '0.9rem',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px',
    },
    searchContainer: {
        position: 'relative',
        marginBottom: '12px',
    },
    searchIcon: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#9ca3af',
    },
    searchInput: {
        width: '100%',
        padding: '10px 12px 10px 40px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '0.95rem',
        outline: 'none',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box',
    },
    userList: {
        maxHeight: '200px',
        overflowY: 'auto',
        border: '1px solid #f3f4f6',
        borderRadius: '8px',
        backgroundColor: '#f9fafb',
    },
    userItem: {
        padding: '10px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        margin: '4px',
        borderRadius: '6px',
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    avatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        objectFit: 'cover',
    },
    avatarPlaceholder: {
        fontSize: '36px',
        color: '#d1d5db',
    },
    userDetails: {
        display: 'flex',
        flexDirection: 'column',
    },
    username: {
        fontSize: '0.9rem',
        fontWeight: '600',
        color: '#111827',
    },
    email: {
        fontSize: '0.8rem',
        color: '#6b7280',
    },
    checkIcon: {
        color: '#2196f3',
        fontSize: '1.1rem',
    },
    select: {
        width: '100%',
        padding: '10px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '0.95rem',
        outline: 'none',
        backgroundColor: '#fff',
    },
    textarea: {
        width: '100%',
        height: '100px',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '0.95rem',
        outline: 'none',
        resize: 'none',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
    },
    hint: {
        fontSize: '0.75rem',
        color: '#6b7280',
        marginTop: '6px',
        fontStyle: 'italic',
    },
    footer: {
        padding: '16px 24px',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        backgroundColor: '#f9fafb',
    },
    cancelBtn: {
        padding: '10px 20px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        backgroundColor: '#fff',
        color: '#374151',
        fontWeight: '600',
        cursor: 'pointer',
    },
    sendBtn: {
        padding: '10px 24px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#1a237e',
        color: '#fff',
        fontWeight: '600',
        cursor: 'pointer',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s',
    },
    loader: {
        padding: '20px',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '0.9rem',
    },
    noResults: {
        padding: '20px',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '0.9rem',
    },
    placeholderText: {
        padding: '20px',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '0.9rem',
    }
};

export default TriggerVerificationModal;
